import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { reservationService, utilService } from '../../../core/services'
import { Button, Pagination, Panel, PanelTitle, Select, EditTableColumn } from '../../atoms'
import { EditTable } from '../../organisms'
import * as interfaces from '../../../static/interfaces'
import * as constants from '../../../static/constants'
import SidebarLayout from '../_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IProps {
  cancel_applications: {
    cancel_applications: IJsonResponse
  }
  user: any
}

const urlParams = new URLSearchParams(window.location.search)
const _status = urlParams.get('status')

const COLUMNS = [
  {
    name: I18n.t('cancel_application.status'),
    field: 'status',
    renderElement: record => (
      <EditTableColumn>
        <strong className={record.status}>{record.status_i18n}</strong>
      </EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.charge_price'),
    field: 'charge_price',
    renderElement: record => (
      <EditTableColumn>{record.tour_reservation.reservable.charge_price}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.amount'),
    field: 'amount',
    renderElement: record => (
      <EditTableColumn>
        <strong>{record.amount.toLocaleString()}</strong>
      </EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.default_amount'),
    field: 'default_amount',
    renderElement: record => (
      <EditTableColumn>{record.default_amount.toLocaleString()}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.reservation_no'),
    field: 'reservation_no',
    renderElement: record => (
      <EditTableColumn>{record.tour_reservation.reservable.no}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.guest_name'),
    field: 'guest_name',
    renderElement: record => (
      <EditTableColumn>{record.tour_reservation.reservable.user.username}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.tour_name'),
    field: 'tour_name',
    renderElement: record => <EditTableColumn>{record.post.name}</EditTableColumn>,
  },
  {
    name: I18n.t('cancel_application.start_at'),
    field: 'start_at',
    renderElement: record => (
      <EditTableColumn>{record.tour_reservation.reservable.start_at}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('cancel_application.requested_at'),
    field: 'requested_at',
    renderElement: record => <EditTableColumn>{record.requested_at_i18n}</EditTableColumn>,
  },
]

const OPTIONS = [
  {
    label: I18n.t('generic.all'),
    value: '',
  },
  {
    label: I18n.t('enums.cancel_application.status.requested'),
    value: 'requested',
  },
  {
    label: I18n.t('enums.cancel_application.status.approved'),
    value: 'approved',
  },
  {
    label: I18n.t('enums.cancel_application.status.declined'),
    value: 'declined',
  },
  {
    label: I18n.t('enums.cancel_application.status.refunded'),
    value: 'refunded',
  },
]

const Index: React.FC<IProps> = props => {
  const { data: user } = utilService.getDataFromJson(props.user)
  const { data: cancelApplications } = utilService.getDataFromJson(
    props.cancel_applications.cancel_applications
  )
  const meta = React.useMemo(() => props.cancel_applications.cancel_applications.meta, [props])

  const [status, setStatus] = React.useState<string>(_status || '')

  const onChangeStatus = e => {
    const params = new URLSearchParams()
    params.set('status', e.target.value)
    location.href = `${location.pathname}?${params.toString()}`
  }
  const onChangePageHandler = page => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', page)
    location.href = `${location.pathname}?${params.toString()}`
  }

  const approveApplication = React.useCallback(async record => {
    if (
      window.confirm(
        '申請を承認すると予約のキャンセルと返金が自動実行されます。承認してよろしいですか？'
      )
    ) {
      try {
        const {
          cancel_application: cancelApplication,
          flush,
        } = await reservationService.approveCancelApplication(record.id)
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
        location.reload()
      } catch (error) {
        console.error(error)
      }
    }
  }, [])

  const declineApplication = React.useCallback(async record => {
    if (window.confirm('申請を却下します。よろしいですか？')) {
      const {
        cancel_application: cancelApplication,
        flush,
      } = await reservationService.declineCancelApplication(record.id)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.reload()
    }
  }, [])

  const renderRecordControls = record => {
    return (
      <Actions>
        {record.status === 'requested' && (
          <>
            <Button small={true} handleClick={() => approveApplication(record)}>
              {I18n.t('generic.approve')}
            </Button>
            <Button
              small={true}
              textColor={constants.COLORS.caution}
              borderColor={constants.COLORS.caution}
              handleClick={() => declineApplication(record)}
            >
              {I18n.t('generic.decline')}
            </Button>
          </>
        )}
      </Actions>
    )
  }

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <Panel
          marginType="dashboard"
          title={<PanelTitle title={I18n.t('cancel_application.index')} />}
        >
          <div className="PanelBody">
            <div className="Filter">
              <Select
                name="status"
                options={OPTIONS}
                defaultValue={status}
                onChangeHandler={onChangeStatus}
              />
            </div>
            <EditTable
              editable={true}
              columns={COLUMNS}
              records={cancelApplications}
              renderRecordControls={renderRecordControls}
              stickyActions={true}
            />
            <Pagination
              onChangePageHandler={onChangePageHandler}
              currentPage={meta.current_page}
              prevPage={meta.prev_page}
              nextPage={meta.next_page}
              totalPages={meta.total_pages}
              totalCount={meta.total_count}
            />
          </div>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .PanelBody {
    min-height: 400px;
  }
  .Filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 16px;
    gap: 16px;
  }
  .Content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  .Section {
    min-width: 220px;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .Buttons {
    width: 100%;
    justify-content: flex-end;
  }
  .requested {
    color: ${constants.COLORS.info};
  }
  .approved {
    color: ${constants.COLORS.success};
  }
  .declined {
    color: ${constants.COLORS.caution};
  }
  .refunded {
    color: ${constants.COLORS.success};
  }
`

const Actions = styled.div`
  display: flex;
`

export default Index
